import {
  useOrganizationsServiceOrganizationsTeamsDestroy,
  useOrganizationsServiceOrganizationsTeamsPartialUpdate,
  useOrganizationsServiceOrganizationsTeamsRetrieveKey
} from '@organization/api/queries'
import { TeamList } from '@organization/api/requests'
import { Loading } from '@organization/components'
import { paths } from '@organization/router'
import { useInvalidateQueries, useSessionContext } from '@shared/hooks'
import { formatDateOrEmpty, showApiErrors } from '@shared/utils'
import {
  App,
  Avatar,
  Button,
  Card,
  Descriptions,
  DescriptionsProps,
  Flex,
  Tag
} from 'antd'
import { Edit, InfoCircle, Profile2User, Trash } from 'iconsax-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { TeamForm } from '../TeamForm'
import { useStyle } from './styles'

type Props = {
  team?: TeamList
}

export const TeamProfile = ({ team }: Props) => {
  const { t } = useTranslation()
  const params = useParams()

  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const { invalidateQueries } = useInvalidateQueries()

  const { isAdmin } = useSessionContext()
  const { styles } = useStyle()
  const { modal, notification } = App.useApp()

  const { mutate: editTeam, isPending } =
    useOrganizationsServiceOrganizationsTeamsPartialUpdate({
      onSuccess: () => {
        invalidateQueries([
          useOrganizationsServiceOrganizationsTeamsRetrieveKey,
          Number(params.teamId)
        ])
        setIsOpen(false)
      },
      onError: (e) => showApiErrors(e)
    })

  const { mutate: deleteTeam } =
    useOrganizationsServiceOrganizationsTeamsDestroy({
      onSuccess: () => {
        navigate(paths.teams.root)
        notification.success({
          message: t('users.team_deleted')
        })
      },
      onError: (e) => showApiErrors(e)
    })

  const onDeleteConfirm = (id: number) => {
    modal.confirm({
      title: t('modal.delete_team'),
      okType: 'danger',
      okText: t('button.delete'),
      cancelText: t('button.cancel'),
      icon: <InfoCircle />,
      onOk: () => deleteTeam({ id })
    })
  }

  if (!team) {
    return <Loading />
  }

  const itemsDescription: DescriptionsProps['items'] = [
    {
      key: 'title',
      label: t('form.item.team_name'),
      children: team.name
    },
    {
      key: 'data_created',
      label: t('table.date_created'),
      children: <Tag>{formatDateOrEmpty(team?.date_created)?.toString()}</Tag>
    },
    {
      key: 'data_updated',
      label: t('table.data_updated'),
      children: <Tag>{formatDateOrEmpty(team?.date_updated)?.toString()}</Tag>
    }
  ]

  return (
    <>
      <Card
        title={t('users.team_info')}
        actions={
          isAdmin
            ? [
                <Flex justify="end" gap={8} key={1}>
                  <Button
                    type="primary"
                    icon={<Edit size={14} />}
                    onClick={() => setIsOpen(true)}
                  >
                    {t('button.edit')}
                  </Button>

                  <Button
                    danger
                    type="primary"
                    icon={<Trash size={14} />}
                    onClick={() => onDeleteConfirm(Number(team?.id))}
                  >
                    {t('button.delete')}
                  </Button>
                </Flex>
              ]
            : undefined
        }
      >
        <Flex gap={20}>
          <Avatar
            size={64}
            shape="square"
            className={styles.cardMetaAvatar}
            src={<Profile2User color="black" size={64} />}
          />

          <Descriptions layout="vertical" column={5} items={itemsDescription} />
        </Flex>
      </Card>

      {isOpen && (
        <TeamForm
          key="rename-team-details"
          title={t('users.edit_team')}
          okText={t('button.edit')}
          team={team}
          isLoading={isPending}
          onSubmit={(values) => {
            if (!team?.id) return
            editTeam({ id: team?.id, requestBody: values })
          }}
          onCancel={() => setIsOpen(false)}
        />
      )}
    </>
  )
}
